import { KeyRound, MoreHorizontal, Plus, Shield, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type User, useUsers } from "@/hooks/use-users";
import { useSession } from "@/lib/auth-client";
import { ChangeRoleDialog } from "./ChangeRoleDialog";
import { CreateUserDialog } from "./CreateUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "resetPassword"; user: User }
  | { type: "changeRole"; user: User }
  | { type: "delete"; user: User };

export function UsersTable() {
  const { data: session } = useSession();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });

  const {
    data: users = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useUsers({ search: debouncedSearch });

  function closeDialog() {
    setDialog({ type: "none" });
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load users"} onRetry={() => refetch()} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search by email..."
          className="flex-1 max-w-sm"
        />
        <Button onClick={() => setDialog({ type: "create" })}>
          <Plus className="mr-2 h-4 w-4" />
          Create User
        </Button>
      </div>

      <DataTable
        headers={[
          { label: "Name" },
          { label: "Email" },
          { label: "Role" },
          { label: "Created", className: "hidden md:table-cell" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState icon={Users} title="No users found" description="Try a different search." />
        }
      >
        {users.map((user) => (
          <tr
            key={user.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 font-medium">{user.name}</td>
            <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
            <td className="px-4 py-3">
              <span
                className={
                  user.role === "admin"
                    ? "inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                    : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                }
              >
                {user.role}
              </span>
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              {new Date(user.createdAt).toLocaleDateString()}
            </td>
            <td className="px-4 py-3 text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" disabled={user.id === session?.user?.id}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setDialog({ type: "changeRole", user })}>
                    <Shield className="h-4 w-4" />
                    Change Role
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDialog({ type: "resetPassword", user })}>
                    <KeyRound className="h-4 w-4" />
                    Reset Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setDialog({ type: "delete", user })}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete User
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          </tr>
        ))}
      </DataTable>

      <CreateUserDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
      />

      {dialog.type === "resetPassword" && (
        <ResetPasswordDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "changeRole" && (
        <ChangeRoleDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "delete" && (
        <DeleteUserDialog open onOpenChange={(open) => !open && closeDialog()} user={dialog.user} />
      )}
    </div>
  );
}
